import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Heading,
  HStack,
  VStack,
  Text,
  Collapse,
} from "@chakra-ui/react";
import CodesetSelect from "./CodesetSelect";
import { HiOutlinePlus, HiOutlineTrash, HiChevronDown, HiChevronRight } from "react-icons/hi";

const PartiesEditor = ({
  parties = [],
  onChange,
  size = "md",
}) => {
  const [localParties, setLocalParties] = useState(parties);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    setLocalParties(parties);
  }, [parties]);

  const handleChange = (index, field, value) => {
    const updated = [...localParties];
    updated[index][field] = value;
    setLocalParties(updated);
    onChange(updated);
  };

  const handleNestedChange = (index, parentField, childField, value) => {
    const updated = [...localParties];
    if (!updated[index][parentField]) {
      updated[index][parentField] = {};
    }
    updated[index][parentField][childField] = value;
    setLocalParties(updated);
    onChange(updated);
  };

  const handleIdentityChange = (partyIdx, identityIdx, field, value) => {
    const updated = [...localParties];
    if (!updated[partyIdx].partyIdentities) {
      updated[partyIdx].partyIdentities = [];
    }
    updated[partyIdx].partyIdentities[identityIdx][field] = value;
    setLocalParties(updated);
    onChange(updated);
  };

  const handleAddParty = () => {
    const newParty = {
      _id: crypto.randomUUID(),
      type: "",
      roleCode: "",
      partyIdentities: [],
      name: "",
      address: {
        type: "",
        numberAndStreet: "",
        subentityCode: "",
        cityName: "",
        postalCode: "",
        countryCode: "",
      },
      contact: {
        description: "",
        roleCode: "",
        personName: "",
        telephone: "",
        fax: "",
        email: "",
      },
      createdDate: new Date().toISOString(),
    };
    const updated = [...localParties, newParty];
    setLocalParties(updated);
    onChange(updated);
  };

  const handleDeleteParty = (index) => {
    const updated = [...localParties];
    updated.splice(index, 1);
    setLocalParties(updated);
    onChange(updated);
  };

  const handleAddIdentity = (partyIdx) => {
    const updated = [...localParties];
    if (!updated[partyIdx].partyIdentities) {
      updated[partyIdx].partyIdentities = [];
    }
    updated[partyIdx].partyIdentities.push({
      id: "",
      agencyCode: "",
    });
    setLocalParties(updated);
    onChange(updated);
  };

  const handleDeleteIdentity = (partyIdx, identityIdx) => {
    const updated = [...localParties];
    updated[partyIdx].partyIdentities.splice(identityIdx, 1);
    setLocalParties(updated);
    onChange(updated);
  };

  const toggleRowExpansion = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} borderColor="gray.500">
      <HStack mb={3} justifyContent="flex-start">
        <Heading size={size}>Parties</Heading>
        <IconButton icon={<HiOutlinePlus />} size={size} onClick={handleAddParty} />
      </HStack>

      <VStack spacing={4} align="stretch">
        {localParties.map((party, partyIdx) => (
          <Box key={party._id || partyIdx} borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
            <HStack mb={2}>
              <IconButton
                icon={expandedRows[partyIdx] ? <HiChevronDown /> : <HiChevronRight />}
                size="sm"
                variant="ghost"
                onClick={() => toggleRowExpansion(partyIdx)}
              />
              <Text fontWeight="bold" flex="1">
                {party.name || `Party ${partyIdx + 1}`}
              </Text>
              <IconButton
                icon={<HiOutlineTrash />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={() => handleDeleteParty(partyIdx)}
              />
            </HStack>

            <Collapse in={expandedRows[partyIdx]} animateOpacity>
              <VStack spacing={3} align="stretch" pl={6}>
                {/* Basic Party Info */}
                <Table variant="simple" size="sm">
                  <Tbody>
                    <Tr>
                      <Td fontWeight="bold" width="150px">Type</Td>
                      <Td>
                        <CodesetSelect
                          codesetCode="B31"
                          value={party.type ? { code: party.type, description: "" } : null}
                          onChange={(val) => handleChange(partyIdx, "type", val.code)}
                          size="sm"
                          height="32px"
                        />
                      </Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">Role Code</Td>
                      <Td>
                        <CodesetSelect
                          codesetCode="3035"
                          value={party.roleCode ? { code: party.roleCode, description: "" } : null}
                          onChange={(val) => handleChange(partyIdx, "roleCode", val.code)}
                          size="sm"
                          height="32px"
                        />
                      </Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="bold">Name</Td>
                      <Td>
                        <Input
                          size="sm"
                          value={party.name || ""}
                          onChange={(e) => handleChange(partyIdx, "name", e.target.value)}
                        />
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>

                {/* Party Identities */}
                <Box>
                  <HStack mb={2}>
                    <Text fontWeight="bold" fontSize="sm">Party Identities</Text>
                    <IconButton
                      icon={<HiOutlinePlus />}
                      size="xs"
                      onClick={() => handleAddIdentity(partyIdx)}
                    />
                  </HStack>
                  {party.partyIdentities && party.partyIdentities.length > 0 && (
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th fontSize="xs">ID</Th>
                          <Th fontSize="xs">Agency Code</Th>
                          <Th></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {party.partyIdentities.map((identity, identityIdx) => (
                          <Tr key={identityIdx}>
                            <Td>
                              <Input
                                size="sm"
                                value={identity.id || ""}
                                onChange={(e) => handleIdentityChange(partyIdx, identityIdx, "id", e.target.value)}
                              />
                            </Td>
                            <Td>
                              <CodesetSelect
                                codesetCode="3055"
                                value={identity.agencyCode ? { code: identity.agencyCode, description: "" } : null}
                                onChange={(val) => handleIdentityChange(partyIdx, identityIdx, "agencyCode", val.code)}
                                size="sm"
                                height="32px"
                              />
                            </Td>
                            <Td>
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="xs"
                                onClick={() => handleDeleteIdentity(partyIdx, identityIdx)}
                              />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </Box>

                {/* Address */}
                <Box>
                  <Text fontWeight="bold" fontSize="sm" mb={2}>Address</Text>
                  <Table variant="simple" size="sm">
                    <Tbody>
                      <Tr>
                        <Td fontWeight="bold" width="150px">Type</Td>
                        <Td>
                          <CodesetSelect
                            codesetCode="3299"
                            value={party.address?.type ? { code: party.address.type, description: "" } : null}
                            onChange={(val) => handleNestedChange(partyIdx, "address", "type", val.code)}
                            size="sm"
                            height="32px"
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Number & Street</Td>
                        <Td>
                          <Input
                            size="sm"
                            value={party.address?.numberAndStreet || ""}
                            onChange={(e) => handleNestedChange(partyIdx, "address", "numberAndStreet", e.target.value)}
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Subentity</Td>
                        <Td>
                          <CodesetSelect
                            codesetCode="A54"
                            value={party.address?.subentityCode ? { code: party.address.subentityCode, description: "" } : null}
                            onChange={(val) => handleNestedChange(partyIdx, "address", "subentityCode", val.code)}
                            size="sm"
                            height="32px"
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">City</Td>
                        <Td>
                          <Input
                            size="sm"
                            value={party.address?.cityName || ""}
                            onChange={(e) => handleNestedChange(partyIdx, "address", "cityName", e.target.value)}
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Postal Code</Td>
                        <Td>
                          <Input
                            size="sm"
                            value={party.address?.postalCode || ""}
                            onChange={(e) => handleNestedChange(partyIdx, "address", "postalCode", e.target.value)}
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Country</Td>
                        <Td>
                          <CodesetSelect
                            codesetCode="iso3166"
                            value={party.address?.countryCode ? { code: party.address.countryCode, description: "" } : null}
                            onChange={(val) => handleNestedChange(partyIdx, "address", "countryCode", val.code)}
                            size="sm"
                            height="32px"
                          />
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>

                {/* Contact */}
                <Box>
                  <Text fontWeight="bold" fontSize="sm" mb={2}>Contact</Text>
                  <Table variant="simple" size="sm">
                    <Tbody>
                      <Tr>
                        <Td fontWeight="bold" width="150px">Description</Td>
                        <Td>
                          <Input
                            size="sm"
                            value={party.contact?.description || ""}
                            onChange={(e) => handleNestedChange(partyIdx, "contact", "description", e.target.value)}
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Role Code</Td>
                        <Td>
                          <CodesetSelect
                            codesetCode="3035"
                            value={party.contact?.roleCode ? { code: party.contact.roleCode, description: "" } : null}
                            onChange={(val) => handleNestedChange(partyIdx, "contact", "roleCode", val.code)}
                            size="sm"
                            height="32px"
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Person Name</Td>
                        <Td>
                          <Input
                            size="sm"
                            value={party.contact?.personName || ""}
                            onChange={(e) => handleNestedChange(partyIdx, "contact", "personName", e.target.value)}
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Telephone</Td>
                        <Td>
                          <Input
                            size="sm"
                            value={party.contact?.telephone || ""}
                            onChange={(e) => handleNestedChange(partyIdx, "contact", "telephone", e.target.value)}
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Fax</Td>
                        <Td>
                          <Input
                            size="sm"
                            value={party.contact?.fax || ""}
                            onChange={(e) => handleNestedChange(partyIdx, "contact", "fax", e.target.value)}
                          />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Email</Td>
                        <Td>
                          <Input
                            size="sm"
                            type="email"
                            value={party.contact?.email || ""}
                            onChange={(e) => handleNestedChange(partyIdx, "contact", "email", e.target.value)}
                          />
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            </Collapse>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export default PartiesEditor;
